import { Box, Button, ButtonGroup, ListItem, Text, Textarea, UnorderedList } from '@chakra-ui/react';
import { CustomFormControl, Modal, formatDate, parseDate } from 'shared-components';
import { IApplicantOfferWithUIState } from '@/store/apply/reducer';
import { IApplicantOfferRequirementTask } from 'types';
import { JSX, useState } from 'react';
import { useTranslation } from "react-i18next";

interface IssueOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionButtonLabel: string;
  offer: IApplicantOfferWithUIState;
  programme: IApplicantOfferWithUIState['programme'];
  conditionReqs: IApplicantOfferRequirementTask[];
  onConfirm: (startText?: string, endText?: string) => void;
  isLoading: boolean;
  defaultStartText?: string;
  defaultEndText?: string;
}

export const IssueOfferModal = ({
  isOpen,
  onClose,
  actionButtonLabel,
  offer,
  programme,
  conditionReqs,
  onConfirm,
  isLoading,
  defaultStartText = '',
  defaultEndText = '',
}: IssueOfferModalProps): JSX.Element | null => {
    const { t } = useTranslation('navigation');
  const [startText, setStartText] = useState(defaultStartText);
  const [endText, setEndText] = useState(defaultEndText);

  // Sync state when defaults change (e.g. modal re-opens with different offer)
  const [prevDefaults, setPrevDefaults] = useState({ startText: defaultStartText, endText: defaultEndText });
  if (defaultStartText !== prevDefaults.startText || defaultEndText !== prevDefaults.endText) {
    setStartText(defaultStartText);
    setEndText(defaultEndText);
    setPrevDefaults({ startText: defaultStartText, endText: defaultEndText });
  }

  const isConditional = conditionReqs.length > 0;
  const pendingProgramme = offer.pendingChanges?.programme;
  const activeProgramme = {
    ...programme,
    ...pendingProgramme,
  };

  const handleConfirm = (): void => {
    onConfirm(startText, endText);
  };

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title={actionButtonLabel}
      footer={
        <ButtonGroup spacing={3} size={'lg'}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="solid" onClick={handleConfirm} isLoading={isLoading}>
            {!conditionReqs.length ? actionButtonLabel : 'Issue conditional offer'}
          </Button>
        </ButtonGroup>
      }
    >
      <Box mb={6}>
        {isConditional ? (
          <>
            <Text>
              {t('navigation:theApplicantWillNowReceiveAnyCon')}
                                      </Text>
            <Text>
              {t('navigation:ifYouWantToIssueAnUnconditionalO')}
                                      </Text>
          </>
        ) : (
          <Text>{t('navigation:allRequirementsAreSatisfiedOrExe')}</Text>
        )}
      </Box>

      <CustomFormControl label={t('navigation:offerLetterOpening')}>
        <Textarea
          value={startText}
          onChange={e => setStartText(e.target.value)}
          rows={4}
          data-testid="start-text-input"
        />
      </CustomFormControl>

      <Box mb={4} color="muted">
        <Text fontSize="sm" mb={2}>
          {t('navigation:fullDetailsOfTheCourseAreAppende')}
                          </Text>
        <UnorderedList fontSize="sm" spacing={1}>
          <ListItem>
            {t('navigation:course')} <b>{activeProgramme?.courseName}</b>
          </ListItem>
          <ListItem>
            {t('navigation:mode')} <b>{activeProgramme?.mode}</b>
          </ListItem>
          <ListItem>
            {t('navigation:entryLevel')} <b>{activeProgramme?.entryPoint}</b>
          </ListItem>
          <ListItem>
            {t('navigation:startDate')}{' '}
            <b>{activeProgramme?.startDate ? formatDate(parseDate(activeProgramme.startDate), 'D MMM YYYY') : ''}</b>
          </ListItem>
          <ListItem>
            {t('navigation:endDate')}{' '}
            <b>{activeProgramme?.endDate ? formatDate(parseDate(activeProgramme.endDate), 'D MMM YYYY') : ''}</b>
          </ListItem>
          <ListItem>
            {t('navigation:courseFee')} <b>{t('navigation:pound')}{activeProgramme?.fee?.toLocaleString() ?? '0'}</b>
          </ListItem>
        </UnorderedList>
      </Box>

      <CustomFormControl label={t('navigation:offerLetterClosing')}>
        <Textarea value={endText} onChange={e => setEndText(e.target.value)} rows={3} data-testid="end-text-input" />
      </CustomFormControl>
    </Modal>
  );
};
